import { Fragment, useEffect, useMemo, useState } from "react";
import { Calendar, ChevronRight, FileSpreadsheet, Printer } from "lucide-react";
import expenseAdvanceApi from "../api/expenseAdvanceApi";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { escapeHtmlForPrint as esc } from "../utils/printHtml";
import ExpenseAttachments, { requiredForLine } from "./ExpenseAttachments";
import Card from "./Card";
import Btn from "./Btn";
import Select2 from "./Select2";
import SearchBar from "./SearchBar";
import T from "../theme/colors";

/**
 * EXPENSE ADVANCES BY PERIOD — the web side of the app's period report.
 *
 * For a period (both ends inclusive) every advance account reads like a
 * cash book:
 *   OPENING  the balance at the end of the day BEFORE the period
 *   GIVEN    advances handed over inside the period
 *   SPENT    everything that left the holder's hand inside the period
 *            (expenses, staff advances / payments, hundekari and transport)
 *   CLOSING  opening + given − spent = the balance at the end of the period
 * The server does the arithmetic (GET /expense-advances/overview and
 * /employee-ledger/{id} with from / to); the page only lays it out.
 *
 *   PeriodReportSection   — every holder for the period, with company totals
 *   EmployeeLedgerSection — one holder's lines; with a period they come
 *                           grouped by day, each day with its own opening and
 *                           closing; with no dates, the whole history
 */

/* ── dates ─────────────────────────────────────────────────────────────── */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad = (n) => String(n).padStart(2, "0");
/** yyyy-mm-dd of a LOCAL date — never toISOString(), which is UTC. */
export const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const shiftIso = (s, days) => {
  const d = parseIso(s);
  return isoDate(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
};
const todayIso = () => isoDate(new Date());

/** 14 Sep 2026 */
export const fmtDay = (s) => {
  if (!s) return "—";
  const d = parseIso(s);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
/** Mon, 14 Sep 2026 */
const fmtLongDay = (s) => {
  if (!s) return "No date";
  const d = parseIso(s);
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** The one-tap periods, computed fresh so "today" is always today. */
export function periodPresets() {
  const now = new Date();
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monday = new Date(t.getFullYear(), t.getMonth(), t.getDate() - ((t.getDay() + 6) % 7));
  const plus = (base, n) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + n);
  return [
    { key: "TODAY", label: "Today", from: isoDate(t), to: isoDate(t) },
    { key: "WEEK", label: "This week", from: isoDate(monday), to: isoDate(t) },
    { key: "LAST_WEEK", label: "Last week", from: isoDate(plus(monday, -7)), to: isoDate(plus(monday, -1)) },
    { key: "MONTH", label: "This month", from: isoDate(new Date(t.getFullYear(), t.getMonth(), 1)), to: isoDate(t) },
    {
      key: "LAST_MONTH", label: "Last month",
      from: isoDate(new Date(t.getFullYear(), t.getMonth() - 1, 1)),
      to: isoDate(new Date(t.getFullYear(), t.getMonth(), 0)),
    },
  ];
}

/** Monday to today — the report's default. */
export const thisWeekPeriod = () => {
  const p = periodPresets()[1];
  return { from: p.from, to: p.to };
};

/* ── money ─────────────────────────────────────────────────────────────── */

const num = (v) => Number(v || 0);
const money = (v) => `₹${num(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (v, sign) => (num(v) > 0 ? `${sign}${money(v)}` : "—");

/* ── shared pieces ─────────────────────────────────────────────────────── */

function PeriodControls({ period, onChange, allowAllTime }) {
  const presets = periodPresets();
  const active = presets.find((p) => p.from === period.from && p.to === period.to)?.key;
  const allTime = !period.from && !period.to;
  // Keep From <= To whichever end moves.
  const setFrom = (v) => onChange({ from: v, to: (period.to && v && period.to < v) ? v : period.to });
  const setTo = (v) => onChange({ from: (period.from && v && period.from > v) ? v : period.from, to: v });
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
      <Calendar size={15} style={{ color: T.text2 }} />
      <input type="date" value={period.from || ""} onChange={(e) => setFrom(e.target.value)} style={dateInput} title="From" />
      <span style={{ color: T.text2, fontSize: 13 }}>to</span>
      <input type="date" value={period.to || ""} onChange={(e) => setTo(e.target.value)} style={dateInput} title="To" />
      {presets.map((p) => (
        <button key={p.key} type="button" onClick={() => onChange({ from: p.from, to: p.to })} style={chipStyle(active === p.key)}>
          {p.label}
        </button>
      ))}
      {allowAllTime && (
        <button type="button" onClick={() => onChange({ from: "", to: "" })} style={chipStyle(allTime)}>
          All time
        </button>
      )}
    </div>
  );
}

function PeriodSummary({ period, opening, given, spent, closing, balanceToday }) {
  const chip = (label, sub, value, color) => (
    <div style={{ flex: 1, minWidth: 150, background: "var(--bg-input)", border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: T.text2 }}>{label}</div>
      <div style={{ fontSize: 11, color: T.text2 }}>{sub}</div>
      <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2, color }}>{money(value)}</div>
    </div>
  );
  const tone = (v, positive) => (num(v) < 0 ? "var(--danger)" : positive);
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "14px 0" }}>
      {chip("Opening", `end of ${fmtDay(shiftIso(period.from, -1))}`, opening, tone(opening, "var(--text-0)"))}
      {chip("Given", "in the period", given, "var(--success)")}
      {chip("Spent", "in the period", spent, "var(--danger)")}
      {chip("Closing", `end of ${fmtDay(period.to)}`, closing, tone(closing, "var(--success)"))}
      {balanceToday != null && period.to !== todayIso() &&
        chip("Balance today", "entries up to today", balanceToday, tone(balanceToday, "var(--text-0)"))}
    </div>
  );
}

function Note({ children, tone }) {
  const warn = tone === "warn";
  return (
    <div style={{
      margin: "12px 0", padding: "10px 12px", borderRadius: 8, fontSize: 13,
      background: warn ? "var(--warn-bg)" : "var(--bg-input)",
      color: warn ? "var(--warn)" : T.text2,
      border: `1px solid ${warn ? "var(--warn-bdr)" : T.border}`,
    }}>
      {children}
    </div>
  );
}

const SERVER_OLD = "The server is not updated yet, so these figures are not for the chosen period — deploy the latest backend.";

/* ── Period report: every holder ───────────────────────────────────────── */

export function PeriodReportSection({ period, onPeriodChange, onOpenEmployee }) {
  const { user, hasPermission } = useAuth();
  const canExport = hasPermission("EXPENSE_ADVANCE", "EXPORT");
  const canPrint = hasPermission("EXPENSE_ADVANCE", "PRINT");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const ready = Boolean(period.from && period.to);

  useEffect(() => {
    setError("");
    if (!ready) {
      setRows([]);
      return undefined;
    }
    // A slower answer for a period the user already left must not land.
    let alive = true;
    setLoading(true);
    expenseAdvanceApi.overview({ from: period.from, to: period.to })
      .then((r) => {
        if (!alive) return;
        const data = r.data?.data ?? r.data;
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!alive) return;
        setRows([]);
        setError(e?.response?.data?.message || "Could not load the report.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [ready, period.from, period.to]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      const moved = num(r.periodEntries) > 0 || num(r.openingBalance) !== 0 || num(r.closingBalance) !== 0;
      if (activeOnly && !moved) return false;
      if (!needle) return true;
      return String(r.employeeName || "").toLowerCase().includes(needle)
        || String(r.employeeCode || "").toLowerCase().includes(needle);
    });
  }, [rows, search, activeOnly]);

  const totals = useMemo(() => shown.reduce((t, r) => ({
    opening: t.opening + num(r.openingBalance),
    given: t.given + num(r.periodGiven),
    spent: t.spent + num(r.periodSpent),
    closing: t.closing + num(r.closingBalance),
  }), { opening: 0, given: 0, spent: 0, closing: 0 }), [shown]);

  const first = rows[0];
  const serverOld = Boolean(first) && !("openingBalance" in first);

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const aoa = [
      ["Expense advances — period report"],
      [`${fmtDay(period.from)} to ${fmtDay(period.to)}  (opening = balance at the end of ${fmtDay(shiftIso(period.from, -1))})`],
      [],
      ["Employee", "Code", "Opening", "Given", "Spent", "Closing"],
      ...shown.map((r) => [
        r.employeeName || "", r.employeeCode || "",
        num(r.openingBalance), num(r.periodGiven), num(r.periodSpent), num(r.closingBalance),
      ]),
      ["TOTAL", "", totals.opening, totals.given, totals.spent, totals.closing],
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Period report");
    XLSX.writeFile(wb, `expense-advances_${period.from}_to_${period.to}.xlsx`);
  };

  const print = () => {
    const body = shown.map((r, i) => `<tr>
        <td>${i + 1}</td>
        <td><b>${esc(r.employeeName || "")}</b>${r.employeeCode ? ` <span class="muted">${esc(r.employeeCode)}</span>` : ""}</td>
        <td class="n">${money(r.openingBalance)}</td>
        <td class="n">${money(r.periodGiven)}</td>
        <td class="n">${money(r.periodSpent)}</td>
        <td class="n"><b>${money(r.closingBalance)}</b></td>
      </tr>`).join("");
    printDocument(`Expense advances ${fmtDay(period.from)} – ${fmtDay(period.to)}`, `
      ${printHeader(user?.companyName, "Expense Advances — Period Report",
        `${fmtDay(period.from)} to ${fmtDay(period.to)} · opening = balance at the end of ${fmtDay(shiftIso(period.from, -1))}`)}
      <table>
        <thead><tr><th>#</th><th>Employee</th><th class="n">Opening</th><th class="n">Given</th><th class="n">Spent</th><th class="n">Closing</th></tr></thead>
        <tbody>${body || `<tr><td colspan="6" class="muted">No employees</td></tr>`}</tbody>
        <tfoot><tr><td></td><td>TOTAL</td><td class="n">${money(totals.opening)}</td><td class="n">${money(totals.given)}</td>
          <td class="n">${money(totals.spent)}</td><td class="n">${money(totals.closing)}</td></tr></tfoot>
      </table>`);
  };

  return (
    <Card>
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
          <PeriodControls period={period} onChange={onPeriodChange} />
          <div style={{ display: "flex", gap: 8 }}>
            {canExport && <Btn variant="secondary" size="sm" icon={<FileSpreadsheet size={13} />} disabled={!ready || loading || shown.length === 0} onClick={exportExcel}>
              Excel
            </Btn>}
            {canPrint && <Btn variant="secondary" size="sm" icon={<Printer size={13} />} disabled={!ready || loading || shown.length === 0} onClick={print}>
              Print
            </Btn>}
          </div>
        </div>

        {!ready && <Note>Pick both dates — or one of the quick periods.</Note>}
        {error && <Note tone="warn">{error}</Note>}
        {serverOld && <Note tone="warn">{SERVER_OLD}</Note>}

        {ready && first && !serverOld && (
          <PeriodSummary
            period={period}
            opening={first.companyOpening}
            given={first.companyPeriodGiven}
            spent={first.companyPeriodSpent}
            closing={first.companyClosing}
            balanceToday={first.companyBalanceToday}
          />
        )}

        {ready && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", margin: "4px 0 10px" }}>
            <SearchBar value={search} onChange={setSearch} placeholder="Search employee…" />
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, color: "var(--text-1)", cursor: "pointer" }}>
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
              Only with money or entries
            </label>
            <span style={{ marginLeft: "auto", fontSize: 12.5, color: T.text2 }}>
              {loading ? "Loading…" : `${shown.length} of ${rows.length} employee${rows.length === 1 ? "" : "s"} · click a row for the day-wise ledger`}
            </span>
          </div>
        )}

        {ready && !loading && rows.length === 0 && !error && (
          <Note>No expense advances have been given yet.</Note>
        )}

        {ready && shown.length > 0 && (
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", opacity: loading ? 0.55 : 1 }}>
            <div style={{ maxHeight: 560, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--bg-input)", position: "sticky", top: 0, zIndex: 1 }}>
                    <th style={th}>Employee</th>
                    <th style={th}>Advances</th>
                    <th style={{ ...th, textAlign: "right" }}>Opening</th>
                    <th style={{ ...th, textAlign: "right" }}>Given</th>
                    <th style={{ ...th, textAlign: "right" }}>Spent</th>
                    <th style={{ ...th, textAlign: "right" }}>Closing</th>
                    <th style={{ ...th, width: 36 }} />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr
                      key={r.employeeId}
                      onClick={() => onOpenEmployee(r.employeeId)}
                      title="Open the day-wise ledger for this period"
                      style={{ cursor: "pointer" }}
                    >
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: "var(--text-0)" }}>{r.employeeName}</div>
                        <div style={{ fontSize: 11.5, color: T.text2 }}>
                          {[r.employeeCode, num(r.periodEntries) === 0 ? "no entries in the period" : null].filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      <td style={{ ...td, color: T.text2, fontSize: 12.5 }}>
                        {num(r.advanceCount)}{r.lastAdvanceDate ? ` · last ${fmtDay(r.lastAdvanceDate)}` : ""}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: num(r.openingBalance) < 0 ? "var(--danger)" : "var(--text-1)" }}>{money(r.openingBalance)}</td>
                      <td style={{ ...td, textAlign: "right", color: num(r.periodGiven) > 0 ? "var(--success)" : T.text2 }}>{signed(r.periodGiven, "+")}</td>
                      <td style={{ ...td, textAlign: "right", color: num(r.periodSpent) > 0 ? "var(--danger)" : T.text2 }}>{signed(r.periodSpent, "−")}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 800, color: num(r.closingBalance) < 0 ? "var(--danger)" : "var(--text-0)" }}>{money(r.closingBalance)}</td>
                      <td style={{ ...td, color: T.text2 }}><ChevronRight size={15} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "var(--bg-input)", position: "sticky", bottom: 0 }}>
                    <td style={{ ...td, fontWeight: 800 }} colSpan={2}>Total{search || activeOnly ? " (shown)" : ""}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 800 }}>{money(totals.opening)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "var(--success)" }}>{money(totals.given)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "var(--danger)" }}>{money(totals.spent)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 900 }}>{money(totals.closing)}</td>
                    <td style={td} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── One holder's ledger — whole history, or a period day by day ────────── */

export function EmployeeLedgerSection({ employeeId, onEmployeeChange, period, onPeriodChange }) {
  const { user, hasPermission } = useAuth();
  const canEditAttachments = hasPermission("EXPENSE_ADVANCE", "EDIT");
  const canExport = hasPermission("EXPENSE_ADVANCE", "EXPORT");
  const canPrint = hasPermission("EXPENSE_ADVANCE", "PRINT");
  const { data: holderData } = useApi(expenseAdvanceApi.holders, [], { cacheKey: "advanceHolders" });
  const employees = Array.isArray(holderData) ? holderData : [];
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const periodMode = Boolean(period.from && period.to);
  const halfPeriod = !periodMode && Boolean(period.from || period.to);

  useEffect(() => {
    setLedger(null);
    setError("");
    if (!employeeId || halfPeriod) return undefined;
    // A slower answer for an employee or period the user already left must not land.
    let alive = true;
    setLoading(true);
    expenseAdvanceApi.employeeLedger(Number(employeeId), periodMode ? { from: period.from, to: period.to } : {})
      .then((r) => {
        if (alive) setLedger(r.data?.data ?? r.data ?? null);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.message || "Could not load the ledger.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [employeeId, periodMode, halfPeriod, period.from, period.to]);

  const rows = useMemo(() => (ledger && Array.isArray(ledger.entries) ? ledger.entries : []), [ledger]);

  // The period's lines grouped by date, oldest day first. A day's opening
  // and closing come from the running balance on its lines, which the
  // server counts over the whole history — true balances, not sums of
  // what is on screen.
  const days = useMemo(() => {
    if (!periodMode) return [];
    const out = [];
    rows.forEach((r) => {
      const given = num(r.given);
      const spent = num(r.spent);
      const after = num(r.balance);
      const key = r.date || "";
      let day = out.length ? out[out.length - 1] : null;
      if (!day || day.date !== key) {
        day = { date: key, rows: [], opening: after - given + spent, given: 0, spent: 0, closing: after };
        out.push(day);
      }
      day.rows.push(r);
      day.given += given;
      day.spent += spent;
      day.closing = after;
    });
    return out;
  }, [rows, periodMode]);

  const employeeName = employees.find((e) => String(e.id) === String(employeeId))?.employeeName || "";
  const serverOld = periodMode && Boolean(ledger) && !("openingBalance" in ledger);
  const periodText = periodMode ? `${fmtDay(period.from)} to ${fmtDay(period.to)}` : "All time";

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const line = (r) => [
      r.date || "", r.particular || "", r.refNo || "", r.paymentMode || "",
      num(r.given), num(r.spent), num(r.balance),
    ];
    const aoa = [
      [`Expense advance ledger — ${employeeName}`],
      [periodText],
      [],
      ["Date", "Particular", "Ref / No", "Mode", "Given", "Spent", "Balance"],
    ];
    if (periodMode) {
      aoa.push([period.from, `Opening balance (end of ${fmtDay(shiftIso(period.from, -1))})`, "", "", "", "", num(ledger?.openingBalance)]);
      rows.forEach((r) => aoa.push(line(r)));
      aoa.push([period.to, "Closing balance", "", "", num(ledger?.periodGiven), num(ledger?.periodSpent), num(ledger?.closingBalance)]);
    } else {
      rows.forEach((r) => aoa.push(line(r)));
      aoa.push(["", "TOTAL", "", "", num(ledger?.totalGiven), num(ledger?.totalSpent), num(ledger?.balance)]);
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Ledger");
    const safe = employeeName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "") || "employee";
    XLSX.writeFile(wb, `advance-ledger_${safe}_${periodMode ? `${period.from}_to_${period.to}` : "all-time"}.xlsx`);
  };

  const print = () => {
    const lineHtml = (r) => `<tr>
        <td>${esc(fmtDay(r.date))}</td>
        <td>${esc(r.particular || "")}${r.detail ? `<div class="muted">${esc(r.detail)}</div>` : ""}</td>
        <td>${esc(r.refNo || "—")}</td>
        <td class="n">${num(r.given) > 0 ? `+${money(r.given)}` : "—"}</td>
        <td class="n">${num(r.spent) > 0 ? `−${money(r.spent)}` : "—"}</td>
        <td class="n"><b>${money(r.balance)}</b></td>
      </tr>`;
    const body = periodMode
      ? days.map((d) => `<tr class="day"><td colspan="6"><b>${esc(fmtLongDay(d.date))}</b>
            <span class="muted">— opening ${money(d.opening)}${d.given ? ` · +${money(d.given)}` : ""}${d.spent ? ` · −${money(d.spent)}` : ""} · closing <b>${money(d.closing)}</b></span></td></tr>
          ${d.rows.map(lineHtml).join("")}`).join("")
      : rows.map(lineHtml).join("");
    const summary = periodMode
      ? `Opening ${money(ledger?.openingBalance)} (end of ${fmtDay(shiftIso(period.from, -1))}) · Given ${money(ledger?.periodGiven)} · Spent ${money(ledger?.periodSpent)} · <b>Closing ${money(ledger?.closingBalance)}</b> (end of ${fmtDay(period.to)})`
      : `Given ${money(ledger?.totalGiven)} · Spent ${money(ledger?.totalSpent)} · <b>Balance ${money(ledger?.balance)}</b>`;
    printDocument(`Advance ledger ${employeeName}`, `
      ${printHeader(user?.companyName, `Expense Advance Ledger — ${esc(employeeName)}`, periodText)}
      <div class="summary">${summary}</div>
      <table>
        <thead><tr><th>Date</th><th>Particular</th><th>Ref / No</th><th class="n">Given</th><th class="n">Spent</th><th class="n">Balance</th></tr></thead>
        <tbody>${body || `<tr><td colspan="6" class="muted">No entries</td></tr>`}</tbody>
      </table>`);
  };

  const lineRow = (r, i) => (
    <tr key={`${r.source || "row"}-${r.entryId ?? i}`}>
      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDay(r.date)}</td>
      <td style={{ ...td, color: T.text0 }}>
        {r.particular}<ExpenseAttachments source={r.attachmentSource} entryId={r.entryId} attachments={r.attachments}
          required={requiredForLine(r.source)}
          canEdit={canEditAttachments} title={`${fmtDay(r.date)} · ${r.particular || ""}`} />
        {r.detail && <div style={{ fontSize: 11.5, color: T.text2 }}>{r.detail}</div>}
      </td>
      <td style={{ ...td, fontFamily: "monospace", fontSize: 11, color: "var(--primary)", whiteSpace: "nowrap" }}>{r.refNo || "—"}</td>
      <td style={{ ...td, textAlign: "right", fontWeight: 600, color: num(r.given) > 0 ? "var(--success)" : T.text2 }}>
        {signed(r.given, "+")}
      </td>
      <td style={{ ...td, textAlign: "right", fontWeight: 600, color: num(r.spent) > 0 ? "var(--danger)" : T.text2 }}>
        {signed(r.spent, "−")}
      </td>
      <td style={{ ...td, textAlign: "right", fontWeight: 800, color: num(r.balance) < 0 ? "var(--danger)" : "inherit" }}>{money(r.balance)}</td>
    </tr>
  );

  const allTimeChip = (label, value, color) => (
    <div style={{ flex: 1, minWidth: 130, background: "var(--bg-input)", border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: T.text2 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2, color: color || "var(--text-0)" }}>{money(value)}</div>
    </div>
  );

  return (
    <Card>
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          <div style={{ flex: "0 1 380px", minWidth: 260 }}>
            <Select2 label="Employee" value={employeeId} onChange={onEmployeeChange}
              placeholder="Whose advance account…"
              options={employees.map((e) => ({ value: String(e.id), label: e.employeeName }))} />
          </div>
          <div style={{ flex: "1 1 460px" }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-1)", marginBottom: 6 }}>Period</div>
            <PeriodControls period={period} onChange={onPeriodChange} allowAllTime />
          </div>
          {employeeId && ledger && (
            <div style={{ display: "flex", gap: 8 }}>
              {canExport && <Btn variant="secondary" size="sm" icon={<FileSpreadsheet size={13} />} onClick={exportExcel}>Excel</Btn>}
              {canPrint && <Btn variant="secondary" size="sm" icon={<Printer size={13} />} onClick={print}>Print</Btn>}
            </div>
          )}
        </div>

        {!employeeId && (
          <div style={{ padding: 30, textAlign: "center", color: T.text2, fontSize: 13 }}>
            Pick an employee to see their expense-advance account.
          </div>
        )}
        {employeeId && halfPeriod && <Note>Pick both dates — or All time for the whole history.</Note>}
        {loading && <div style={{ padding: 24, textAlign: "center", color: T.text2, fontSize: 13 }}>Loading ledger…</div>}
        {error && <Note tone="warn">{error}</Note>}

        {employeeId && !loading && ledger && (
          <>
            {serverOld && <Note tone="warn">{SERVER_OLD}</Note>}
            {periodMode && !serverOld && (
              <PeriodSummary
                period={period}
                opening={ledger.openingBalance}
                given={ledger.periodGiven}
                spent={ledger.periodSpent}
                closing={ledger.closingBalance}
                balanceToday={ledger.balanceToday}
              />
            )}
            {!periodMode && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "14px 0" }}>
                {allTimeChip("Total Given", ledger.totalGiven, "var(--success)")}
                {allTimeChip("Total Spent", ledger.totalSpent, "var(--danger)")}
                {allTimeChip("Balance In Hand", ledger.balance, num(ledger.balance) < 0 ? "var(--danger)" : "var(--success)")}
              </div>
            )}

            {rows.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: T.text2, fontSize: 13 }}>
                {periodMode
                  ? `No entries between ${fmtDay(period.from)} and ${fmtDay(period.to)}.`
                  : "No advances or expenses yet for this employee."}
              </div>
            )}
            {rows.length > 0 && (
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ maxHeight: 560, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "var(--bg-input)", position: "sticky", top: 0, zIndex: 1 }}>
                        <th style={th}>Date</th>
                        <th style={th}>Particular</th>
                        <th style={th}>Ref / No</th>
                        <th style={{ ...th, textAlign: "right" }}>Given</th>
                        <th style={{ ...th, textAlign: "right" }}>Spent</th>
                        <th style={{ ...th, textAlign: "right" }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodMode
                        ? days.map((d) => (
                          <Fragment key={d.date || "no-date"}>
                            <tr style={{ background: "var(--primary-light)" }}>
                              <td style={{ ...td, fontWeight: 800, color: "var(--text-0)", whiteSpace: "nowrap" }}>{fmtLongDay(d.date)}</td>
                              <td style={{ ...td, color: T.text2 }} colSpan={2}>
                                Opening {money(d.opening)}
                                {d.given ? ` · +${money(d.given)}` : ""}
                                {d.spent ? ` · −${money(d.spent)}` : ""}
                              </td>
                              <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "var(--success)" }}>{d.given ? money(d.given) : ""}</td>
                              <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "var(--danger)" }}>{d.spent ? money(d.spent) : ""}</td>
                              <td style={{ ...td, textAlign: "right", fontWeight: 900, color: d.closing < 0 ? "var(--danger)" : "var(--text-0)" }}>
                                {money(d.closing)}
                              </td>
                            </tr>
                            {d.rows.map(lineRow)}
                          </Fragment>
                        ))
                        : rows.map(lineRow)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/* ── printing ──────────────────────────────────────────────────────────── */

function printHeader(companyName, title, subtitle) {
  return `<div class="head">
      <div class="co">${esc(companyName || "")}</div>
      <div class="title">${title}</div>
      <div class="muted">${esc(subtitle || "")}</div>
    </div>`;
}

function printDocument(title, bodyHtml) {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 18px; font-size: 13px; }
      .head { margin-bottom: 10px; }
      .co { font-size: 18px; font-weight: 800; }
      .title { font-size: 15px; font-weight: 700; margin-top: 2px; }
      .muted { color: #555; font-size: 12px; }
      .summary { margin: 8px 0 12px; padding: 8px 10px; border: 1px solid #ccc; background: #f6f6f2; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #bbb; padding: 5px 8px; text-align: left; vertical-align: top; }
      th { background: #eee; font-size: 12px; text-transform: uppercase; }
      .n { text-align: right; white-space: nowrap; }
      tr.day td { background: #f3efe6; }
      tfoot td { font-weight: 800; background: #eee; }
      .foot { margin-top: 12px; font-size: 11px; color: #555; text-align: right; }
    </style></head><body>
    ${bodyHtml}
    <div class="foot">Printed ${esc(new Date().toLocaleString("en-IN"))}</div>
    <script>window.onload = function () { setTimeout(function () { window.print(); }, 200); };</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) {
    window.alert("Allow pop-ups for this site to print.");
    return;
  }
  w.document.write(html);
  w.document.close();
}

/* ── styles ────────────────────────────────────────────────────────────── */

const th = {
  padding: "8px 12px", fontSize: 11, fontWeight: 700, color: T.text2, textTransform: "uppercase",
  borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap", textAlign: "left",
};
const td = { padding: "8px 12px", borderBottom: `1px solid ${T.border}`, fontSize: 13, verticalAlign: "top" };

const dateInput = {
  padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13.5,
  background: "var(--bg-input)", color: "var(--text-0)", fontFamily: "inherit",
};

const chipStyle = (active) => ({
  padding: "5px 11px", borderRadius: 16, cursor: "pointer", fontSize: 12.5, fontWeight: 600,
  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
  background: active ? "var(--primary-light)" : "var(--bg-card)",
  color: active ? "var(--primary)" : "var(--text-1)",
});
